import { useCallback, useMemo } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { startOfMonth } from 'date-fns';
import { useHandleShowDialogs } from 'forms/hooks';
import { useDispatch } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import RadioGroup from '@gef-ui/form-material/RadioGroup';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import { getLabelMessage, getMessage, getMessage as gM } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import fetch from 'features/common/fetch';
import { resetPage, search } from 'features/views/taskStatistics/actions';
import { radioOptions, SEARCH_TYPE } from 'features/views/taskStatistics/constants';
import { FIELDS, TASK_STATISTICS_FORM_NAME } from 'features/views/taskStatistics/form';
import { getExactlyXYearsAgoAsDateObj } from 'utils/apiDateTime';

export const TaskStatisticsForm = () => {
	const next = useControlNext(TASK_STATISTICS_FORM_NAME);
	const formData = useFormData();

	const dispatch = useDispatch();

	const formattedRadioOptions = useMemo(
		() => radioOptions.map((option) => ({ ...option, name: getMessage(option.name) })),
		[],
	);

	const handleSubmit = useCallback(async () => {
		dispatch(search());
	}, [dispatch]);

	const handleReset = useCallback(() => {
		dispatch(resetPage());
	}, [dispatch]);

	const handleTypeChange = useCallback(
		(e, value) => {
			if (value === SEARCH_TYPE.status) {
				next(setValue({ fieldName: FIELDS.dateFrom, value: null }));
				next(setValue({ fieldName: FIELDS.dateTo, value: null }));
			}
		},
		[next],
	);
	const handleShowDialogsForSearchSubmit = useHandleShowDialogs({
		page: TASK_STATISTICS_FORM_NAME,
		continueFn: handleSubmit,
	});

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Grid size={4}>
						<RadioGroup
							fieldName={FIELDS.statType}
							options={formattedRadioOptions}
							row
							onChange={handleTypeChange}
							validationDisplay={!formData?.dateFrom ? validationDisplayTypes.HIDDEN : undefined}
							spacing={2}
						/>
					</Grid>
					<Grid size={4}>
						<DatePickerFullWidth
							fieldName={FIELDS.dateFrom}
							label={gM('taskStatistics:statsFrom')}
							maxDate={new Date()}
							minDate={startOfMonth(getExactlyXYearsAgoAsDateObj(-1))}
							disabled={formData?.statType === SEARCH_TYPE.status}
							required={formData?.statType !== SEARCH_TYPE.status}
							validationDisplay={!formData?.dateFrom ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={4}>
						<DatePickerFullWidth
							fieldName={FIELDS.dateTo}
							label={gM('taskStatistics:statsTo')}
							maxDate={new Date()}
							minDate={startOfMonth(getExactlyXYearsAgoAsDateObj(-1))}
							disabled={formData?.statType === SEARCH_TYPE.status}
							required={formData?.statType !== SEARCH_TYPE.status}
							validationDisplay={!formData?.dateTo ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={12}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={3}>
								<Button variant="outlined" onClick={handleReset} fullWidth>
									{getLabelMessage('common:resetButton')}
								</Button>
							</Grid>
							<Grid size={3}>
								<ComponentLoader fetchStorePath={fetch.constants.urls.taskStatistics} ignoreErrors>
									<SubmitButton
										variant="contained"
										onClick={handleSubmit}
										onClickError={handleShowDialogsForSearchSubmit}
										fullWidth
										text={getLabelMessage('common:show')}
									/>
								</ComponentLoader>
							</Grid>
						</Grid>
					</Grid>
					<Grid size={12} />
				</Grid>
			</Grid>
		</Grid>
	);
};

export default TaskStatisticsForm;
